import React, { useState } from "react";
import { ImportPreviewRow } from "@/lib/api/imports";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { formatNumberWithCommas } from "@/lib/formatters";

interface ImportPreviewGridProps {
  rows: ImportPreviewRow[];
  onRowsChange: (rows: ImportPreviewRow[]) => void;
  itemTypes: Array<{ id: string; name: string }>;
  locations: Array<{ id: string; name: string; code?: string | null }>;
  collections: Array<{ id: string; name: string; code?: string | null }>;
  conditions: Array<{ id: string; code: string; label: string }>;
}

export function ImportPreviewGrid({
  rows,
  onRowsChange,
  itemTypes,
  locations,
  collections,
  conditions,
}: ImportPreviewGridProps) {
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const updateRow = (index: number, updates: Partial<ImportPreviewRow>) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], ...updates };
    onRowsChange(newRows);
  };

  const toggleRowSelection = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  const selectAll = () => {
    const allFound = rows.filter((r) => r.status === "FOUND").map((r) => r.rowIndex);
    setSelectedRows(new Set(allFound));
  };

  const deselectAll = () => {
    setSelectedRows(new Set());
  };

  const bulkUpdate = (field: keyof ImportPreviewRow, value: any) => {
    const newRows = rows.map((row) =>
      selectedRows.has(row.rowIndex) ? { ...row, [field]: value } : row
    );
    onRowsChange(newRows);
    setSelectedRows(new Set());
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "FOUND":
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "NOT_FOUND":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "NEEDS_REVIEW":
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "secondary"> = {
      FOUND: "default",
      NOT_FOUND: "destructive",
      NEEDS_REVIEW: "secondary",
    };

    return (
      <Badge variant={variants[status] || "default"} className="flex items-center gap-1">
        {getStatusIcon(status)}
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const getCatalogStatusBadge = (row: ImportPreviewRow) => {
    if (row.existingBiblioId) {
      const count = row.existingCopyCount ?? 0;
      return (
        <Badge variant="secondary">
          Exists ({formatNumberWithCommas(count)} {count === 1 ? "copy" : "copies"})
        </Badge>
      );
    }

    return <Badge variant="outline">New</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedRows.size > 0 && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded flex items-center justify-between">
          <span className="text-sm font-medium">
            {selectedRows.size} row{selectedRows.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => bulkUpdate("locationCode", "")}
              className="text-sm text-primary hover:underline"
            >
              Clear Location
            </button>
            <button
              onClick={() => bulkUpdate("collectionCode", "")}
              className="text-sm text-primary hover:underline"
            >
              Clear Collection
            </button>
            <button
              onClick={() => bulkUpdate("conditionCode", "")}
              className="text-sm text-primary hover:underline"
            >
              Clear Condition
            </button>
            <button onClick={deselectAll} className="text-sm text-primary hover:underline">
              Deselect All
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    checked={selectedRows.size === rows.filter((r) => r.status === "FOUND").length}
                    onCheckedChange={(checked) => (checked ? selectAll() : deselectAll())}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                  Catalog status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">ISBN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Author</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Copies</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                  Collection
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Condition</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                  Subjects <span className="text-red-500">*</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">
                  Acquisition <span className="text-red-500">*</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row, idx) => (
                <tr key={row.rowIndex} className={row.status !== "FOUND" ? "bg-gray-50" : ""}>
                  <td className="px-4 py-3">
                    {row.status === "FOUND" && (
                      <Checkbox
                        checked={selectedRows.has(row.rowIndex)}
                        onCheckedChange={() => toggleRowSelection(row.rowIndex)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                  <td className="px-4 py-3">{getCatalogStatusBadge(row)}</td>
                  <td className="px-4 py-3 text-sm font-mono">{row.isbn}</td>
                  <td className="px-4 py-3 text-sm">
                    {row.metadata?.title || <span className="text-gray-400">Not found</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {row.metadata?.authors?.join(", ") || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      type="number"
                      min="1"
                      value={row.copies}
                      onChange={(e) => updateRow(idx, { copies: parseInt(e.target.value) || 1 })}
                      className="w-20"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      value={row.locationCode || ""}
                      onChange={(e) => updateRow(idx, { locationCode: e.target.value })}
                      placeholder="Location code"
                      className="w-32"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      value={row.collectionCode || ""}
                      onChange={(e) => updateRow(idx, { collectionCode: e.target.value })}
                      placeholder="Collection code"
                      className="w-32"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      value={row.conditionCode || ""}
                      onChange={(e) => updateRow(idx, { conditionCode: e.target.value })}
                      placeholder="Condition code"
                      className="w-32"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      value={row.subjects ?? row.metadata?.subjects?.join("; ") ?? ""}
                      onChange={(e) => updateRow(idx, { subjects: e.target.value })}
                      placeholder="e.g. Fiction; History"
                      className="w-40"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      value={row.acquisitionMode || ""}
                      onChange={(e) => updateRow(idx, { acquisitionMode: e.target.value })}
                      placeholder="PURCHASE"
                      className="w-28"
                      disabled={row.status !== "FOUND"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
